import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "onpoint_cookie_consent";

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing the banner
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "declined");
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-4xl mx-auto bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl">
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="hidden sm:flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full flex-shrink-0">
                  <Cookie className="w-5 h-5 text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
                        <Cookie className="w-4 h-4 text-primary sm:hidden" />
                        Cookie Notice
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                        We use cookies and similar technologies to enhance your experience, 
                        analyze site usage, and assist in our marketing efforts. By clicking 
                        &ldquo;Accept All,&rdquo; you consent to our use of cookies. View our{" "}
                        <Link 
                          to="/privacy" 
                          className="text-primary hover:underline"
                        >
                          Privacy Policy
                        </Link>{" "}
                        for more information.
                      </p>
                    </div>

                    {/* Close button (mobile) */}
                    <button
                      onClick={handleDecline}
                      className="sm:hidden p-1 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Close cookie notice"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                      onClick={handleAccept}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium px-4 py-2 h-auto rounded-sm"
                      data-testid="cookie-accept-btn"
                    >
                      <Shield className="w-3 h-3 mr-1.5" />
                      Accept All
                    </Button>
                    <Button
                      onClick={handleDecline}
                      variant="outline"
                      className="border-white/10 text-foreground hover:bg-white/5 text-xs font-medium px-4 py-2 h-auto rounded-sm"
                      data-testid="cookie-decline-btn"
                    >
                      Essential Only
                    </Button>
                    <Link 
                      to="/privacy" 
                      className="hidden sm:inline-flex items-center text-xs text-muted-foreground hover:text-foreground transition-colors px-2"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
